import { useCallback } from 'react';
import { notification } from 'antd';
import type { VcsApi } from '@env0/vcs-service/api';
import { Popup } from 'utils/popup.utils';
import { intl } from 'components/localization';
import { useGetAuthorizationUrl } from 'hooks/use-get-authorization-url';
import type { OAuthResult } from 'components/organizations/vcs/vcs-connection-modal/vcs-connection-modal-form-oauth-authorization-button';
import { useGenerateGitAuthToken } from 'stores/rq/vcs-auth-tokens';
import type { GitProviderTypes } from 'types/api.types';

type UseVcsOAuthPopupProps = {
  connectionType: VcsApi.ConnectionType;
  vcsType: VcsApi.VcsType;
  accessScope?: string;
  onSuccess: (result: OAuthResult) => void;
  onError?: (error: string) => void;
  onTokenProcessingChange?: (isProcessing: boolean) => void;
};

const TOKEN_BACKED_OAUTH_VCS_TYPES: VcsApi.VcsType[] = ['GitLab', 'AzureDevOps'];

export const useVcsOAuthPopup = ({
  connectionType,
  vcsType,
  accessScope,
  onSuccess,
  onError,
  onTokenProcessingChange
}: UseVcsOAuthPopupProps) => {
  const authorizationUrl = useGetAuthorizationUrl({ connectionType, vcsType });
  const { mutateAsync: generateGitAuthToken } = useGenerateGitAuthToken();

  const openOAuthPopup = useCallback(() => {
    Popup.open({
      url: authorizationUrl,
      closeByMessage: 'authorizationCallback',
      onBlock: () => {
        const errorMsg = intl.formatMessage({ id: 'vcs.authorize-button.blocked' });
        notification.error({
          message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
          description: errorMsg
        });
        onError?.(errorMsg);
      },
      onClosed: async result => {
        if (!result?.accessScope) {
          const errorMsg = result?.error || intl.formatMessage({ id: 'vcs.authorize-button.window-closed' });
          notification.error({
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
          return;
        }
        if (accessScope && accessScope !== result.accessScope) {
          const errorMsg = intl.formatMessage(
            {
              id: 'organization.settings.agents.vcs-connections-modal.connection.oauth.accessScopeWrong'
            },
            { accessScope }
          );
          notification.error({
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
          return;
        }

        let oauthResult: OAuthResult = result as OAuthResult;
        if (TOKEN_BACKED_OAUTH_VCS_TYPES.includes(vcsType) && oauthResult.authorizationId) {
          onTokenProcessingChange?.(true);
          try {
            const token = await generateGitAuthToken({
              gitProvider: vcsType as GitProviderTypes,
              authorizationId: oauthResult.authorizationId
            });
            oauthResult = { ...oauthResult, tokenId: token.id, authorizationId: undefined };
          } catch (error) {
            const errorMsg = (error as Error)?.message ?? 'Failed to create token';
            notification.error({
              message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
              description: errorMsg
            });
            onError?.(errorMsg);
            return;
          } finally {
            onTokenProcessingChange?.(false);
          }
        }

        notification.success({
          message: intl.formatMessage({ id: 'vcs.authorize-button.success.title' }),
          description: intl.formatMessage({ id: 'vcs.authorize-button.success.description' })
        });
        onSuccess(oauthResult);
      },
      features: { height: 700, width: 700 }
    });
  }, [authorizationUrl, accessScope, onSuccess, onError, onTokenProcessingChange, vcsType, generateGitAuthToken]);

  return { openOAuthPopup };
};
